import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { reminderLook } from '../components/CategoryIcon'
import { api, type Reminder } from '../lib/api'
import { formatDistance } from '../lib/location'
import { useStore } from '../lib/store'
import './MapView.css'

/** A glowing category-coloured pin, drawn inline so it can be themed. */
function pinIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: 'pin-wrap',
    html: `<span class="pin" style="--pin:${color}"></span>`,
    iconSize: [26, 34],
    iconAnchor: [13, 32],
  })
}

const userIcon = L.divIcon({
  className: 'pin-wrap',
  html: '<span class="me"></span>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
})

export function MapView() {
  const { reminders, nearbyIds, fix, refresh, categories } = useStore()
  const hostRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layerRef = useRef<L.LayerGroup | null>(null)
  const [sheet, setSheet] = useState<Reminder | null>(null)
  const [busy, setBusy] = useState(false)
  // Once the user has panned or pinched, the camera is theirs. Without this the
  // auto-fit below would haul them back to the ring bounds on the next redraw.
  const userMovedRef = useRef(false)
  // Distinguishes our own setView/fitBounds calls from a real gesture, so
  // programmatic camera moves don't count as the user taking over.
  const selfMoveRef = useRef(false)
  // The last set of rings we fitted to, so we fit when the *reminders* change
  // rather than every time a new GPS reading lands.
  const fittedKeyRef = useRef<string | null>(null)

  // Create the map exactly once. This effect used to depend on `fix`; because
  // its cleanup nulls `mapRef`, every geolocation tick tore the whole Leaflet
  // instance down and built a new one at a hardcoded zoom 13 — the map
  // "refreshing" every second or two (APP-01) and pinch-to-zoom appearing dead
  // because the gesture's listeners were destroyed underneath it (APP-09).
  useEffect(() => {
    if (!hostRef.current || mapRef.current) return
    const map = L.map(hostRef.current, {
      center: [37.7749, -122.4194],
      zoom: 13,
      zoomControl: false,
      attributionControl: true,
      // Stated explicitly rather than left to defaults: these are the gestures
      // the map exists for, and a silent regression here is invisible in tests.
      touchZoom: true,
      dragging: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
    })
    // Voyager keeps green parks and blue water while staying light and low
    // contrast — much closer to the mockup than Positron's near-greyscale.
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      maxZoom: 19,
    }).addTo(map)
    layerRef.current = L.layerGroup().addTo(map)
    mapRef.current = map

    const claim = () => {
      if (!selfMoveRef.current) userMovedRef.current = true
    }
    map.on('dragstart', claim)
    map.on('zoomstart', claim)

    return () => {
      map.off('dragstart', claim)
      map.off('zoomstart', claim)
      map.remove()
      mapRef.current = null
      layerRef.current = null
    }
  }, [])

  // Redraw pins + radius rings whenever the data or position changes.
  useEffect(() => {
    const map = mapRef.current
    const layer = layerRef.current
    if (!map || !layer) return
    layer.clearLayers()

    if (fix) {
      L.marker([fix.lat, fix.lon], { icon: userIcon, interactive: false }).addTo(layer)
    }

    // Fit to the trigger *rings*, not just the pins — otherwise two nearby
    // stores zoom the map in so far that the rings sit entirely off-screen and
    // the one thing the map exists to show is invisible.
    let bounds: L.LatLngBounds | null = null
    const extend = (b: L.LatLngBounds) => {
      bounds = bounds ? bounds.extend(b) : b
    }

    for (const r of reminders) {
      const s = r.nearest_store
      if (!s || (s.lat === 0 && s.lon === 0)) continue
      const color = reminderLook(r, categories).color

      const ring = L.circle([s.lat, s.lon], {
        radius: r.radius_m,
        color,
        weight: 1.4,
        opacity: 0.75,
        fillColor: color,
        fillOpacity: 0.09,
        dashArray: '5 7',
        interactive: false,
      }).addTo(layer)
      extend(ring.getBounds())

      L.marker([s.lat, s.lon], { icon: pinIcon(color) })
        .addTo(layer)
        .on('click', () => setSheet(r))
    }

    if (fix) extend(L.latLngBounds([[fix.lat, fix.lon], [fix.lat, fix.lon]]))

    // Redrawing pins is free; moving the camera is not. Only auto-fit when the
    // thing being framed actually changed, and never after the user has taken
    // the map over themselves.
    const key = reminders
      .map((r) => `${r.id}:${r.nearest_store?.lat ?? ''},${r.nearest_store?.lon ?? ''}@${r.radius_m}`)
      .join('|')
      .concat(fix ? '+fix' : '')
    if (userMovedRef.current || key === fittedKeyRef.current) return
    fittedKeyRef.current = key

    selfMoveRef.current = true
    try {
      if (bounds) {
        map.fitBounds((bounds as L.LatLngBounds).pad(0.08), { animate: false })
      } else if (fix) {
        map.setView([fix.lat, fix.lon], 14, { animate: false })
      }
    } finally {
      selfMoveRef.current = false
    }
  }, [reminders, fix, categories])

  // Arriving somewhere should surface the sheet without a tap.
  useEffect(() => {
    if (sheet) return
    const hit = reminders.find((r) => nearbyIds.has(r.id))
    if (hit) setSheet(hit)
  }, [nearbyIds, reminders, sheet])

  async function act(fn: () => Promise<unknown>) {
    setBusy(true)
    try {
      await fn()
      await refresh()
      setSheet(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mapscreen">
      <div ref={hostRef} className="mapscreen__canvas" />

      <button
        className="icon-btn mapscreen__locate"
        aria-label="Centre on me"
        onClick={() => fix && mapRef.current?.setView([fix.lat, fix.lon], 15)}
      >
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round">
          <path d="M21 3 3 10.5l7.5 3 3 7.5Z" />
        </svg>
      </button>

      {reminders.length === 0 && (
        <div className="mapscreen__hint glass">
          Add a reminder and its stores will show up here with their trigger rings.
        </div>
      )}

      {sheet && (
        <div className="sheet glass" role="dialog" aria-label="Nearby reminder">
          <span className="sheet__grip" />
          <p className="sheet__eyebrow">
            {nearbyIds.has(sheet.id) ? "You're near" : 'Reminder at'}
          </p>
          <h2>{sheet.nearest_store?.name ?? sheet.merchant_label}</h2>
          <p className="sheet__item">
            <i style={{ background: reminderLook(sheet, categories).color }} />
            {sheet.title}
          </p>
          <p className="sheet__dist">
            {formatDistance(sheet.nearest_store?.distance_m)}
            {sheet.nearest_store?.is_open === false && ' · closed right now'}
          </p>
          <button
            className="btn btn--primary"
            disabled={busy}
            onClick={() => act(() => api.completeReminder(sheet.id))}
          >
            Mark Done
          </button>
          <button
            className="btn btn--ghost"
            disabled={busy}
            onClick={() => act(() => api.snoozeReminder(sheet.id, 30))}
          >
            Snooze (30 min)
          </button>
          <button className="sheet__close" onClick={() => setSheet(null)}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  )
}
